import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from '../hooks/use-toast';
import { ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import ScrollReveal from './ScrollReveal';

const FinalCTA = () => {
  const navigate = useNavigate();
  const [regNumber, setRegNumber] = useState('');
  const [kilometers, setKilometers] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!regNumber || !kilometers) {
      toast({
        title: "Vennligst fyll ut begge feltene",
        variant: "destructive"
      });
      return;
    }
    navigate('/selg-bilen', { state: { regNumber, kilometers } });
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Animated background elements */}
      <motion.div 
        className="absolute top-0 left-1/4 w-96 h-96 bg-yellow-200 rounded-full blur-3xl opacity-20"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 50, 0],
          y: [0, 30, 0]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div 
        className="absolute bottom-0 right-1/4 w-96 h-96 bg-yellow-100 rounded-full blur-3xl opacity-15"
        animate={{
          scale: [1, 1.3, 1],
          x: [0, -50, 0],
          y: [0, -30, 0]
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <ScrollReveal direction="up" className="text-center mb-12">
          <motion.div 
            className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full mb-6"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ 
              type: "spring",
              stiffness: 200,
              damping: 15
            }}
            whileHover={{ scale: 1.05 }}
          >
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-semibold">Start nå – det tar bare 30 sekunder</span>
          </motion.div>

          <motion.h2 
            className="text-5xl font-black text-gray-900 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ 
              type: "spring",
              stiffness: 100,
              damping: 20,
              delay: 0.1
            }}
          >
            Klar til å selge?
          </motion.h2>
          <motion.p 
            className="text-xl text-gray-600"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ 
              type: "spring",
              stiffness: 100,
              damping: 20,
              delay: 0.2
            }}
          >
            Få tilbud fra 2 000+ forhandlere nå
          </motion.p>
        </ScrollReveal>

        <motion.form 
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ 
            type: "spring",
            stiffness: 100,
            damping: 20,
            delay: 0.3
          }}
        >
          <motion.div 
            className="bg-white border-2 border-gray-200 rounded-3xl p-8 shadow-xl relative overflow-hidden"
            whileHover={{ 
              borderColor: "#fbbf24",
              boxShadow: "0 25px 50px rgba(0,0,0,0.1)"
            }}
            transition={{ duration: 0.3 }}
          >
            {/* Decorative gradient overlay */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-yellow-100 to-transparent rounded-bl-full opacity-50"></div>

            <div className="grid sm:grid-cols-2 gap-4 mb-6 relative z-10">
              <motion.div
                whileFocus={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <Input
                  type="text"
                  placeholder="Registreringsnummer"
                  value={regNumber}
                  onChange={(e) => setRegNumber(e.target.value.toUpperCase())}
                  className="h-16 text-lg uppercase bg-gray-50 border-2 border-gray-200 focus:border-gray-900 focus:bg-white transition-all"
                />
              </motion.div>
              <motion.div
                whileFocus={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <Input
                  type="number"
                  placeholder="Kilometerstand"
                  value={kilometers}
                  onChange={(e) => setKilometers(e.target.value)}
                  className="h-16 text-lg bg-gray-50 border-2 border-gray-200 focus:border-gray-900 focus:bg-white transition-all"
                />
              </motion.div>
            </div>
            
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              <Button 
                type="submit" 
                className="w-full h-16 bg-gradient-to-r from-gray-900 to-gray-700 hover:from-gray-800 hover:to-gray-600 text-white text-lg font-bold shadow-xl relative z-10 group"
              >
                <span>Kom i gang</span>
                <motion.div
                  animate={{ x: [0, 5, 0] }}
                  transition={{ 
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <ArrowRight className="ml-2 w-5 h-5" />
                </motion.div>
              </Button>
            </motion.div>

            <motion.p 
              className="text-xs text-gray-500 text-center mt-4 relative z-10"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              100% gratis og uforpliktende • Ingen skjulte kostnader
            </motion.p>
          </motion.div>
        </motion.form>
      </div>
    </section>
  );
};

export default FinalCTA;